
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { PartyPopper, MessageCircle, Heart, Menu, X } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

export function FriendlyCasualHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-blue-50 text-gray-800 font-sans">
      <header className="container mx-auto px-6 py-5 flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-600">HeyThere!</h2>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="#" className="font-medium hover:text-blue-600">
            Features
          </a>
          <a href="#" className="font-medium hover:text-blue-600">
            Pricing
          </a>
          <Button className="bg-gray-800 text-white hover:bg-gray-700 rounded-full">
            Log In
          </Button>
        </nav>
        <div className="md:hidden">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {isMenuOpen && (
        <div className="md:hidden bg-blue-100 border-y">
          <nav className="container mx-auto flex flex-col items-start gap-4 p-6">
            <a href="#" className="font-medium hover:text-blue-600 text-lg">
              Features
            </a>
            <a href="#" className="font-medium hover:text-blue-600 text-lg">
              Pricing
            </a>
            <Button className="bg-gray-800 text-white hover:bg-gray-700 rounded-full w-full mt-2">
              Log In
            </Button>
          </nav>
        </div>
      )}

      <main>
        <section className="py-20 text-center container mx-auto px-6">
          <Badge
            variant="outline"
            className="mb-4 border-blue-200 bg-blue-100 text-blue-700"
          >
            <PartyPopper className="w-4 h-4 mr-2" />
            Now even easier to use!
          </Badge>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 leading-tight">
            Finally, a social app that's actually social.
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-lg text-gray-600">
            Connect with your friends, share what's up, and have fun without
            all the noise. Simple, right?
          </p>
          <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-4">
            <input
              type="email"
              placeholder="Enter your email to join"
              className="w-full sm:w-auto px-4 py-3 rounded-full border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            />
            <Button
              size="lg"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-full w-full sm:w-auto"
            >
              Get Started for Free
            </Button>
          </div>
          <div className="mt-8">
            <Image
              src="https://picsum.photos/seed/friends/1200/600"
              alt="A group of diverse friends laughing and using their phones together"
              width={1200}
              height={600}
              className="rounded-2xl shadow-xl"
              data-ai-hint="friends laughing"
            />
          </div>
        </section>

        <section className="py-24 bg-white px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl font-bold text-gray-900">
                Everything you need, nothing you don't.
              </h2>
              <p className="mt-4 text-gray-600">
                We focus on the good stuff and leave out the rest.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-10">
              <div className="text-center">
                <div className="bg-blue-100 rounded-2xl p-6 inline-block mb-4">
                  <MessageCircle className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold">Real Conversations</h3>
                <p className="mt-2 text-gray-500">
                  Group chats that are easy to manage and fun to be in. GIFs and
                  emoji reactions included!
                </p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-2xl p-6 inline-block mb-4">
                  <Heart className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold">No-Stress Sharing</h3>
                <p className="mt-2 text-gray-500">
                  Share updates with your close friends without worrying about
                  likes or algorithms.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-yellow-100 rounded-2xl p-6 inline-block mb-4">
                  <PartyPopper className="w-10 h-10 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold">Fun Hangouts</h3>
                <p className="mt-2 text-gray-500">
                  Plan events, movie nights, or just spontaneous get-togethers,
                  all inside the app.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900">
                Your Questions, Answered
              </h2>
            </div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem
                value="item-1"
                className="bg-white rounded-lg p-2 mb-4"
              >
                <AccordionTrigger className="font-bold text-lg text-left">
                  Is it really free?
                </AccordionTrigger>
                <AccordionContent className="pt-2 text-gray-600">
                  Yep! The core features of HeyThere are completely free to use.
                  We may offer some premium cosmetic features in the future,
                  but the main experience will always be free.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                value="item-2"
                className="bg-white rounded-lg p-2 mb-4"
              >
                <AccordionTrigger className="font-bold text-lg text-left">
                  How do you make money?
                </AccordionTrigger>
                <AccordionContent className="pt-2 text-gray-600">
                  Right now, we're focused on building a great product. We do
                  not sell your data or show you ads. Our future plans involve
                  optional paid features that enhance the experience.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                value="item-3"
                className="bg-white rounded-lg p-2 mb-4"
              >
                <AccordionTrigger className="font-bold text-lg text-left">
                  Can I use this on my computer?
                </AccordionTrigger>
                <AccordionContent className="pt-2 text-gray-600">
                  Absolutely! HeyThere works seamlessly on your phone, tablet,
                  and desktop browser, so you can stay connected wherever you
                  are.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>
      </main>
      <footer className="bg-gray-800 text-white py-12 px-4">
        <div className="container mx-auto text-center">
          <h3 className="text-3xl font-bold">Ready to give it a try?</h3>
          <p className="mt-2 text-gray-300">
            It's free and takes 30 seconds to sign up.
          </p>
          <Button size="lg" className="mt-6 bg-blue-600 hover:bg-blue-700">
            Let's Go!
          </Button>
          <div className="mt-12 pt-8 border-t border-gray-700 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400 gap-4">
            <p>&copy; {new Date().getFullYear()} HeyThere Inc. Have a nice day! 👋</p>
            <div className="flex gap-4">
              <a href="#" className="hover:text-white">
                Terms
              </a>
              <a href="#" className="hover:text-white">
                Privacy
              </a>
              <a href="#" className="hover:text-white">
                Twitter
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
